import { useState, useEffect } from "react";

const C = {
  pink: "#F7A1C4", mint: "#9ED9C3", yellow: "#FFD966",
  blue: "#9CCBFF", lilac: "#D7B7F6", ink: "#1F1F1F", cream: "#FFF8F1",
  pinkSoft: "#FDE8F0", mintSoft: "#E6F5EE", yellowSoft: "#FFF7DC",
  blueSoft: "#E4F0FF", lilacSoft: "#F0E5FC",
};
const heading = "'Baloo 2', cursive, sans-serif";
const body = "'Nunito', sans-serif";
const navMap = { "Home": "home", "Meet The Bunch": "meet", "Classes": "classes", "Gallery": "gallery", "Reviews": "reviews", "Parties": "parties", "Merch": "merch", "Schools": "schools", "Contact": "contact" };


const IMG = {
  pinky: "/images/characters/pinky.webp",
  dottie: "/images/characters/dottie.webp",
  misty: "/images/characters/misty.webp",
  minty: "/images/characters/minty.webp",
  sunny: "/images/characters/sunny.webp",
  logo: "/images/logo.webp",
  cowboy: "/images/themes/cowboy.jpeg",
  safari: "/images/themes/safari.jpeg",
};

const characters = [
  { name: "Pinky Pop", color: C.pink, soft: C.pinkSoft, img: IMG.pinky },
  { name: "Sunny Ray", color: C.yellow, soft: C.yellowSoft, img: IMG.sunny },
  { name: "Dottie", color: C.blue, soft: C.blueSoft, img: IMG.dottie },
  { name: "Minty Wiggle", color: C.mint, soft: C.mintSoft, img: IMG.minty },
  { name: "Misty Cloud", color: C.lilac, soft: C.lilacSoft, img: IMG.misty },
];

const AnimChar = ({ img, name, size = 120, delay = 0, style = {} }) => (
  <div style={{
    width: size, height: size,
    animation: "idleWobble 1.8s ease-in-out infinite",
    animationDelay: `${delay}s`,
    transformOrigin: "center bottom",
    ...style,
  }}>
    <img src={img} alt={name} style={{
      width: "100%", height: "100%", objectFit: "contain",
      filter: "drop-shadow(0 4px 10px rgba(0,0,0,0.08))",
    }} />
  </div>
);

const Section = ({ children, bg = C.cream, style = {} }) => (
  <section style={{ background: bg, padding: "56px 24px", ...style }}>
    <div style={{ maxWidth: 900, margin: "0 auto" }}>{children}</div>
  </section>
);

const RainbowHeading = ({ children, size = 28, tag = "h2" }) => {
  const s = {
    fontFamily: heading, fontSize: size, fontWeight: 800, textAlign: "center",
    lineHeight: 1.3, margin: 0,
    background: `linear-gradient(90deg, ${C.pink}, ${C.yellow}, ${C.mint}, ${C.blue}, ${C.lilac}, ${C.pink})`,
    backgroundSize: "200% 100%",
    WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
    backgroundClip: "text", animation: "rainbowSlide 4s linear infinite",
  };
  if (tag === "h1") return <h1 style={s}>{children}</h1>;
  if (tag === "h3") return <h3 style={s}>{children}</h3>;
  return <h2 style={s}>{children}</h2>;
};

/* ═══ NAV ═══ */
const Nav = () => {
  const links = ["Home", "Meet The Bunch", "Classes", "Gallery", "Reviews", "Parties", "Merch", "Schools", "Contact"];
  return (
    <nav style={{
      background: C.cream, padding: "10px 24px",
      position: "sticky", top: 0, zIndex: 100,
      borderBottom: `2px solid ${C.pink}15`,
      boxShadow: "0 2px 16px rgba(0,0,0,0.03)",
    }}>
      <div style={{
        maxWidth: 900, margin: "0 auto",
        display: "flex", alignItems: "center", justifyContent: "space-between",
      }}>
        <img src={IMG.logo} alt="The Funky Bunch" style={{ height: 46, objectFit: "contain", cursor: "pointer" }} onClick={() => { window.location.hash = "home"; }} />
        <div style={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
          {links.map(l => (
            <span key={l} style={{
              fontSize: 11.5, fontFamily: body, fontWeight: 600,
              color: l === "Home" ? C.pink : C.ink + "88",
              padding: "5px 9px", borderRadius: 14,
              background: l === "Home" ? C.pinkSoft : "transparent",
              cursor: "pointer",
            }} onClick={() => { window.location.hash = navMap[l]; }}>{l}</span>
          ))}
          <span style={{
            background: C.pink, color: "#fff", padding: "7px 18px",
            borderRadius: 50, fontFamily: heading, fontWeight: 700, fontSize: 12,
            cursor: "pointer", marginLeft: 4,
          }} onClick={() => { window.location.hash = "classes"; }}>Book Now</span>
        </div>
      </div>
    </nav>
  );
};

/* ═══ HERO ═══ */
const Hero = () => (
  <Section bg={`linear-gradient(180deg, ${C.cream} 0%, #FFF0F5 35%, #F0EAFF 65%, #E8F5FF 100%)`}
    style={{ padding: "48px 24px 40px" }}>
    <div style={{ textAlign: "center" }}>
      <div style={{ display: "flex", justifyContent: "center", marginBottom: 20 }}>
        <img src={IMG.logo} alt="The Funky Bunch" style={{
          width: 220, maxWidth: "55%",
          filter: "drop-shadow(0 4px 12px rgba(0,0,0,0.06))",
        }} />
      </div>
      <h1 style={{
        fontFamily: heading, fontSize: 26, fontWeight: 800,
        color: C.ink, lineHeight: 1.35, margin: "0 auto 10px", maxWidth: 480,
      }}>
        A playful preschool dance class in Ashford, Kent
      </h1>
      <p style={{
        fontFamily: body, fontSize: 16, color: C.ink + "aa",
        lineHeight: 1.6, margin: "0 auto 28px", maxWidth: 420,
      }}>
        Designed to build confidence, creativity and joy through movement.
      </p>
      <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
        <button style={{
          background: C.mint, color: "#fff", border: "none",
          padding: "14px 34px", borderRadius: 50,
          fontFamily: heading, fontWeight: 700, fontSize: 16, cursor: "pointer",
          boxShadow: `0 4px 16px ${C.mint}55`,
        }} onClick={() => { window.location.hash = "classes"; }}>Book a Class</button>
        <button style={{
          background: "transparent", color: C.ink, border: `2px solid ${C.pink}`,
          padding: "14px 34px", borderRadius: 50,
          fontFamily: heading, fontWeight: 700, fontSize: 16, cursor: "pointer",
        }} onClick={() => { window.location.hash = "meet"; }}>Meet The Bunch</button>

      </div>
      {/* Characters row - BIGGER */}
      <div style={{
        display: "flex", justifyContent: "center", gap: 12,
        marginTop: 36, flexWrap: "wrap",
      }}>
        {characters.map((c, i) => (
          <AnimChar key={i} img={c.img} name={c.name} size={100} delay={i * 0.2} />
        ))}
      </div>
    </div>
  </Section>
);


/* ═══ MAY HALF TERM WIDGET ═══ */

/* ═══ WHY ═══ */
const WhySection = () => {
  const reasons = [
    { title: "No Pressure", desc: "Children join in at their own pace. No right or wrong moves.", color: C.pink, soft: C.pinkSoft, char: IMG.pinky },
    { title: "Feel-Good Focus", desc: "Building confidence, creativity and emotional wellbeing.", color: C.lilac, soft: C.lilacSoft, char: IMG.misty },
    { title: "Learn Through Play", desc: "Games, popular music and simple routines made for little ones.", color: C.mint, soft: C.mintSoft, char: IMG.minty },
  ];
  return (
    <Section bg={C.cream}>
      <RainbowHeading size={24}>Why The Funky Bunch?</RainbowHeading>
      <div style={{ height: 20 }} />
      <div style={{ display: "flex", gap: 20, justifyContent: "center", flexWrap: "wrap" }}>
        {reasons.map((r, i) => (
          <div key={i} style={{
            flex: "1 1 220px", maxWidth: 260,
            background: r.soft, borderRadius: 24,
            padding: "28px 20px 24px", textAlign: "center",
            border: `2px solid ${r.color}30`,
          }}>
            {/* BIGGER character images */}
            <div style={{ width: 100, height: 100, margin: "0 auto 16px" }}>
              <img src={r.char} alt="" style={{ width: "100%", height: "100%", objectFit: "contain", cursor: "pointer" }} onClick={() => { window.location.hash = "home"; }} />
            </div>
            <div style={{ fontFamily: heading, fontWeight: 700, fontSize: 17, color: C.ink, marginBottom: 6 }}>{r.title}</div>
            <div style={{ fontFamily: body, fontSize: 13, color: C.ink + "99", lineHeight: 1.6 }}>{r.desc}</div>
          </div>
        ))}
      </div>
    </Section>
  );
};

/* ═══ CLASS BREAKDOWN - CONCEPT A: TIMELINE ═══ */
const ClassBreakdown = () => {
  const colors = [C.pink, C.yellow, C.mint, C.blue, C.lilac, C.pink];
  const softs = [C.pinkSoft, C.yellowSoft, C.mintSoft, C.blueSoft, C.lilacSoft, C.pinkSoft];
  const steps = [
    "A friendly warm-up song",
    "Fun movement and dance activity",
    "Music and imagination",
    "Time to dance freely and creatively",
    "A mini disco",
    "A calm cool-down and goodbye",
  ];
  return (
    <Section bg="#fff">
      <RainbowHeading size={22}>What Happens in a Class?</RainbowHeading>
      <p style={{
        fontFamily: body, fontSize: 14, color: C.ink + "aa",
        lineHeight: 1.7, textAlign: "center", maxWidth: 520,
        margin: "8px auto 32px",
      }}>
        Our classes are built around music, movement and imagination. Through games, dance routines and creative play, children explore simple moves in a safe, friendly environment, building confidence and having fun every step of the way.
      </p>
      <div style={{
        display: "flex", gap: 28, alignItems: "center",
        flexWrap: "wrap", justifyContent: "center",
      }}>
        {/* Timeline */}
        <div style={{ position: "relative", paddingLeft: 28, flex: "1 1 300px", maxWidth: 440 }}>
          {/* Rainbow gradient line */}
          <div style={{
            position: "absolute", left: 10, top: 12, bottom: 12, width: 4, borderRadius: 2,
            background: `linear-gradient(180deg, ${C.pink}, ${C.yellow}, ${C.mint}, ${C.blue}, ${C.lilac}, ${C.pink})`,
          }} />
          {steps.map((s, i) => (
            <div key={i} style={{
              display: "flex", alignItems: "center", gap: 16,
              marginBottom: 14, position: "relative",
            }}>
              {/* Dot on timeline */}
              <div style={{
                width: 16, height: 16, borderRadius: "50%",
                background: colors[i],
                border: `3px solid #fff`,
                boxShadow: `0 0 0 2px ${colors[i]}44`,
                position: "absolute", left: -24,
                flexShrink: 0,
              }} />
              {/* Step card */}
              <div style={{
                background: softs[i], padding: "14px 20px",
                borderRadius: 16, flex: 1,
                border: `1.5px solid ${colors[i]}25`,
              }}>
                <span style={{
                  fontFamily: body, fontWeight: 700,
                  fontSize: 15, color: C.ink,
                }}>{s}</span>
              </div>
            </div>
          ))}
        </div>
        {/* BIGGER Dottie */}
        <div style={{ flex: "0 0 auto" }}>
          <AnimChar img={IMG.dottie} name="Dottie" size={200} delay={0.3} />
        </div>
      </div>
    </Section>
  );
};

/* ═══ THIS WEEK'S THEME ═══ */
const ThemeTeaser = () => (
  <Section bg={C.cream} style={{ padding: "48px 24px" }}>
    <div style={{
      maxWidth: 560, margin: "0 auto",
      borderRadius: 24, overflow: "hidden",
      border: `2px solid ${C.yellow}30`,
      background: "#fff",
    }}>
      <img src={IMG.cowboy} alt="Cowboy theme" style={{ width: "100%", height: 240, objectFit: "cover" }} />
      <div style={{ padding: "24px 28px", textAlign: "center" }}>
        <div style={{
          fontSize: 11, fontFamily: body, fontWeight: 700,
          color: C.ink + "77", letterSpacing: 2, textTransform: "uppercase", marginBottom: 6,
        }}>This Week's Theme</div>
        <RainbowHeading size={24} tag="h3">Cowboy Dance!</RainbowHeading>
        <p style={{ fontFamily: body, fontSize: 13, color: C.ink + "99", lineHeight: 1.6, margin: "8px 0 20px" }}>
          Yeehaw! The Funky Bunch are heading to the Wild West. Grab your cowboy hats and get ready to stomp, gallop and lasso your way across the dancefloor!
        </p>
        <div style={{
          display: "flex", alignItems: "center", gap: 14,
          background: C.cream, borderRadius: 16, padding: "12px 16px",
          textAlign: "left",
        }}>
          <div style={{
            width: 56, height: 56, borderRadius: 12, overflow: "hidden", flexShrink: 0,
            position: "relative",
          }}>
            <img src={IMG.safari} alt="" style={{
              width: "100%", height: "100%", objectFit: "cover",
              filter: "blur(8px) saturate(0.7)", transform: "scale(1.2)",
            }} />
            <div style={{
              position: "absolute", inset: 0,
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 18, color: "#fff", textShadow: "0 1px 4px rgba(0,0,0,0.3)",
            }}>?</div>
          </div>
          <div>
            <div style={{ fontSize: 10, fontFamily: body, fontWeight: 700, color: C.ink + "66", textTransform: "uppercase", letterSpacing: 1 }}>Next week</div>
            <div style={{ fontFamily: heading, fontWeight: 700, fontSize: 14, color: C.ink + "88" }}>Something exciting is coming...</div>
          </div>
        </div>
      </div>
    </div>
  </Section>
);

/* ═══ CHARACTER PARADE - BIGGER ═══ */
const CharacterParade = () => (
  <Section bg="#fff" style={{ padding: "44px 24px" }}>
    <RainbowHeading size={22}>Meet The Bunch</RainbowHeading>
    <div style={{ height: 20 }} />
    <div style={{ display: "flex", justifyContent: "center", gap: 20, flexWrap: "wrap" }}>
      {characters.map((c, i) => (
        <div key={i} style={{ textAlign: "center" }}>
          <AnimChar img={c.img} name={c.name} size={110} delay={i * 0.15} />
          <div style={{ fontFamily: heading, fontWeight: 700, fontSize: 13, color: C.ink, marginTop: 4 }}>{c.name}</div>
        </div>
      ))}
    </div>
    <div style={{ textAlign: "center", marginTop: 20 }}>
      <span style={{ fontSize: 13, fontFamily: body, fontWeight: 600, color: C.lilac, cursor: "pointer" }} onClick={() => { window.location.hash = "meet"; }}>
        Get to know everyone
      </span>
    </div>
  </Section>
);

/* ═══ REVIEWS ═══ */
const Reviews = () => {
  const [idx, setIdx] = useState(0);
  const reviews = [
    { text: "Lovely little class! Full of fun and energy which is perfect for my lively two year old! Sophie and Brittany put lots of thought into the sessions and the children seem to really engage and enjoy it! Lots of fun, laughter and dancing! It's a big hit with my daughter.", author: "Chantelle" },
    { text: "My Grandaughter was so shy, but Sophie spoke to her as she was doing the dancing, and she was joining in a few of the dances. The props were fun for the kids to dance with. My Josie wants to go next week, so you've done a good job in making it fun for the kids! Well done Funky Bunch teachers.", author: "Victoria" },
    { text: "Attended the first week class and my little girl loved it and is asking to go back already! Sophie and Brittany have thought of all the small details for the children and have put so much effort in for them and the dances and music and they are both really engaging with the kids! Can't wait to return next week!", author: "Liz" },
  ];
  useEffect(() => {
    const t = setInterval(() => setIdx(i => (i + 1) % reviews.length), 4500);
    return () => clearInterval(t);
  }, []);
  return (
    <Section bg={C.cream} style={{ padding: "48px 24px" }}>
      <RainbowHeading size={20}>What Parents Say</RainbowHeading>
      <div style={{ height: 8 }} />
      <div style={{ maxWidth: 480, margin: "0 auto", textAlign: "center", minHeight: 160 }}>
        <div style={{ fontSize: 20, color: C.yellow, marginBottom: 12 }}>{"★".repeat(5)}</div>
        <p key={idx} style={{
          fontFamily: body, fontSize: 15, color: C.ink, lineHeight: 1.7,
          fontStyle: "italic", marginBottom: 10,
          animation: "fadeIn 0.5s ease",
        }}>
          "{reviews[idx].text}"
        </p>
        <div style={{ fontFamily: heading, fontWeight: 700, fontSize: 12, color: C.ink + "77" }}>
          — {reviews[idx].author}
        </div>
        <div style={{ display: "flex", justifyContent: "center", gap: 6, marginTop: 16 }}>
          {reviews.map((_, i) => (
            <div key={i} onClick={() => setIdx(i)} style={{
              width: 8, height: 8, borderRadius: "50%", cursor: "pointer",
              background: i === idx ? C.pink : C.pink + "33",
              transition: "background 0.3s",
            }} />
          ))}
        </div>
      </div>
    </Section>
  );
};

/* ═══ CTA ═══ */
const CtaBanner = () => (
  <Section bg={`linear-gradient(135deg, ${C.pinkSoft}, ${C.lilacSoft}, ${C.blueSoft})`} style={{ padding: "48px 24px" }}>
    <div style={{ textAlign: "center" }}>
      <RainbowHeading size={22}>Ready to join the fun?</RainbowHeading>
      <p style={{ fontFamily: body, fontSize: 14, color: C.ink + "99", margin: "8px 0 24px" }}>
        Spaces are limited to 15 children per class. Book your spot today!
      </p>
      <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
        <button style={{
          background: C.pink, color: "#fff", border: "none",
          padding: "14px 32px", borderRadius: 50,
          fontFamily: heading, fontWeight: 700, fontSize: 15, cursor: "pointer",
        }} onClick={() => { window.location.hash = "classes"; }}>Book a Class</button>
        <button style={{
          background: "#fff", color: C.ink, border: `2px solid ${C.mint}`,
          padding: "14px 32px", borderRadius: 50,
          fontFamily: heading, fontWeight: 700, fontSize: 15, cursor: "pointer",
        }} onClick={() => { window.location.hash = "parties"; }}>Book a Birthday Party</button>
      </div>
    </div>
  </Section>
);

/* ═══ FOOTER ═══ */
const Footer = () => (
  <footer style={{ background: C.cream, padding: "36px 24px 20px", borderTop: `2px solid ${C.pink}15` }}>
    <div style={{ maxWidth: 900, margin: "0 auto" }}>
      <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 28, marginBottom: 24 }}>
        <div>
          <div style={{ fontFamily: heading, fontWeight: 700, fontSize: 14, color: C.ink, marginBottom: 6 }}>Find Us</div>
          <div style={{ fontFamily: body, fontSize: 12, color: C.ink + "88", lineHeight: 1.8 }}>
            Lumina Dance Company<br />Eden Business Centre<br />South Stour Ave, Ashford<br />Kent TN23 7RS
          </div>
        </div>
        <div>
          <div style={{ fontFamily: heading, fontWeight: 700, fontSize: 14, color: C.ink, marginBottom: 6 }}>Classes</div>
          <div style={{ fontFamily: body, fontSize: 12, color: C.ink + "88", lineHeight: 1.8 }}>
            Term-time Thursdays<br />10:30 – 11:15
          </div>
        </div>
        <div>
          <div style={{ fontFamily: heading, fontWeight: 700, fontSize: 14, color: C.ink, marginBottom: 6 }}>Get in Touch</div>
          <div style={{ fontFamily: body, fontSize: 12, color: C.ink + "88", lineHeight: 1.8 }}>
            thefunkybunchashford@gmail.com
          </div>
          <div style={{ display: "flex", gap: 10, marginTop: 6 }}>
            <a href="https://www.facebook.com/thefunkybunchkent" target="_blank" rel="noopener noreferrer"><svg width="20" height="20" viewBox="0 0 24 24" fill={C.blue}><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg></a>
            <a href="https://www.instagram.com/thefunkybunchkent" target="_blank" rel="noopener noreferrer"><svg width="20" height="20" viewBox="0 0 24 24" fill={C.pink}><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg></a>
          </div>
        </div>
      </div>
      <div style={{
        height: 3, borderRadius: 2, marginBottom: 14,
        background: `linear-gradient(90deg, ${C.pink}, ${C.yellow}, ${C.mint}, ${C.blue}, ${C.lilac})`,
      }} />
      <div style={{ fontFamily: body, fontSize: 10, color: C.ink + "55", textAlign: "center" }}>
        © 2026 The Funky Bunch Kent. All rights reserved.
      </div>
    </div>
  </footer>
);

/* ═══ MAIN ═══ */
export default function FunkyBunchHome() {
  return (
    <div style={{ background: C.cream, minHeight: "100vh" }}>
      <link href="https://fonts.googleapis.com/css2?family=Baloo+2:wght@400;500;600;700;800&family=Nunito:wght@400;500;600;700;800&display=swap" rel="stylesheet" />
      <style>{`
        * { margin: 0; padding: 0; box-sizing: border-box; }
        @keyframes idleWobble {
          0%, 100% { transform: rotate(0deg) scaleX(1) scaleY(1); }
          20% { transform: rotate(-3deg) scaleX(1.02) scaleY(0.98); }
          50% { transform: rotate(3deg) scaleX(0.98) scaleY(1.02); }
          80% { transform: rotate(-2deg) scaleX(1.01) scaleY(0.99); }
        }
        @keyframes specialPulse {
          0%, 100% { transform: scale(1); box-shadow: 0 0 15px rgba(247,161,196,0.2); }
          50% { transform: scale(1.01); box-shadow: 0 0 25px rgba(247,161,196,0.4); }
        }
        @keyframes specialGlow {
          0%, 100% { box-shadow: 0 0 20px rgba(247,161,196,0.2); }
          50% { box-shadow: 0 0 35px rgba(247,161,196,0.4); }
        }
        @keyframes sparkle {
          0%, 100% { opacity: 0.3; transform: scale(0.8); }
          50% { opacity: 1; transform: scale(1.2); }
        }
        @keyframes rainbowSlide {
          0% { background-position: 0% 50%; }
          100% { background-position: 200% 50%; }
        }
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(6px); }
          to { opacity: 1; transform: translateY(0); }
        }
        button:hover { transform: scale(1.03); transition: transform 0.2s; }
        button:active { transform: scale(0.98); }
      `}</style>
      <Nav />
      <Hero />

      {/* Monday interest modal */}
      
      <WhySection />
      <ClassBreakdown />
      <CharacterParade />
      <Reviews />
      <CtaBanner />
      <Footer />
    </div>
  );
}
